import React, { useEffect, useRef, useState } from "react";

import HttpManager from "../../../services/HttpManager";
import { EquipoMensaje } from "../../../services/v2/prospects.service";
import WappIcono from "../../../components/wappIcono";
import { abrirArchivo, ArchivoParaVer } from "../../../components/enrollment/visorArchivo";
import { errorTexto } from "./salesHelpers";
import { StickerImg } from "./stickers";
import { BotonEmoji, insertarEnCursor, teclaEnter } from "./equipoEmojis";
import { useCrecer } from "./crecer";
import { idsMencionados, ListaMenciones, Persona, primerNombre, useMenciones } from "./menciones";

/**
 * Fotos, videos y archivos de WappTeam (Esteban, 2026-10-09: "el visor y colocación de files está weird, no deja enviar
 * múltiples archivos"): miniaturas de tamaño contenido, álbum en cuadrícula, video con portada y botón de reproducir,
 * documentos como tarjeta, y al tocar se abre el visor de WAPP con ← → para pasar entre todos los de la conversación.
 * Lo usan la pantalla completa (equipoView) y las ventanitas (wappteamDock).
 */

export const tamano = (b: number | null | undefined) => (!b ? "" : b >= 1048576 ? (b / 1048576).toFixed(1) + " MB" : Math.max(1, Math.round(b / 1024)) + " KB");

// URL firmada por mensaje (dura 30 min en el servidor; aquí se reusa 20).
const cache = new Map<string, { p: Promise<{ url: string; nombre: string; mime: string }>; at: number }>();
export const urlDe = (campusId: number, id: number) => {
	const k = campusId + ":" + id;
	const c = cache.get(k);
	if (c && Date.now() - c.at < 20 * 60000) return c.p;
	const p = HttpManager.getInstance().prospectsService.equipoArchivo(campusId, id).then((r) => r.data);
	p.catch(() => cache.delete(k));
	cache.set(k, { p, at: Date.now() });
	return p;
};

const esVisual = (m: EquipoMensaje) => m.file_tipo === "image" || m.file_tipo === "video";
const tieneArchivo = (m: EquipoMensaje) => !!m.file_tipo && !m.deleted_at && m.file_tipo !== "audio" && m.file_tipo !== "sticker";

/** Abre el visor en este mensaje, con todos los archivos de la conversación para pasar con ← →. */
export const abrirGaleria = (campusId: number, todos: EquipoMensaje[], m: EquipoMensaje) => {
	const lista = todos.filter(tieneArchivo);
	const item = (x: EquipoMensaje): ArchivoParaVer => ({
		nombre: x.file_name || (x.file_tipo === "video" ? "Video" : x.file_tipo === "image" ? "Foto" : "Archivo"),
		mime: x.file_mime, video: x.file_tipo === "video", origen: "WappTeam",
		obtenerUrl: esVisual(x) ? () => urlDe(campusId, x.id).then((d) => d.url) : undefined,
		cargar: async () => {
			const d = await urlDe(campusId, x.id);
			const blob = await (await fetch(d.url)).blob();
			return { blob, nombre: x.file_name };
		},
	});
	const galeria = lista.map(item);
	const i = Math.max(0, lista.findIndex((x) => x.id === m.id));
	abrirArchivo({ ...(galeria[i] || item(m)), galeria, indice: i });
};

/** Cuadro de foto o video con su URL firmada; el video lleva portada (primer cuadro) y botón de reproducir. */
export const Miniatura: React.FC<{ campusId: number; m: EquipoMensaje; onAbrir: () => void; clase?: string; extra?: number }> = ({ campusId, m, onAbrir, clase, extra }) => {
	const [url, setUrl] = useState<string | null>(null);
	const [err, setErr] = useState<string | null>(null);
	const caja = useRef<HTMLButtonElement>(null);
	useEffect(() => {
		let vivo = true;
		// Solo cuando entra a la vista (conversaciones largas importadas de Cliq no piden cientos de URLs de golpe).
		const el = caja.current;
		const pedir = () => urlDe(campusId, m.id).then((d) => { if (vivo) setUrl(d.url); }).catch((e) => { if (vivo) setErr(errorTexto(e)); });
		if (!el || typeof IntersectionObserver === "undefined") { pedir(); return () => { vivo = false; }; }
		const io = new IntersectionObserver((es) => { if (es.some((x) => x.isIntersecting)) { io.disconnect(); pedir(); } }, { rootMargin: "300px" });
		io.observe(el);
		return () => { vivo = false; io.disconnect(); };
	}, [campusId, m.id]);
	return (
		<button ref={caja} type="button" className={"eqm-mini " + (m.file_tipo === "video" ? "video " : "") + (clase || "")} onClick={onAbrir}
			aria-label={m.file_tipo === "video" ? "Ver video" : "Ver foto"}>
			{err ? <span className="eqm-err">{err}</span>
				: !url ? <span className="eqm-cargando" />
				: m.file_tipo === "video" ? <video src={url + "#t=0.001"} preload="metadata" muted playsInline tabIndex={-1} />
				: <img src={url} alt={m.file_name || "Foto"} loading="lazy" />}
			{m.file_tipo === "video" ? <span className="eqm-play" aria-hidden="true"><svg viewBox="0 0 24 24" width="22" height="22"><path d="M8 5.5v13l11-6.5z" fill="currentColor" /></svg></span> : null}
			{m.file_tipo === "video" && m.duracion ? <span className="eqm-dur">{Math.floor(m.duracion / 60)}:{String(m.duracion % 60).padStart(2, "0")}</span> : null}
			{extra ? <span className="eqm-mas">+{extra}</span> : null}
		</button>
	);
};

const ICONO_EXT: { [k: string]: string } = { pdf: "pdf", doc: "doc", docx: "doc", xls: "cell", xlsx: "cell", csv: "cell", ppt: "deck", pptx: "deck", zip: "zip", rar: "zip" };

/** PDF y demás archivos: tarjeta con ícono, nombre y peso. */
export const TarjetaArchivo: React.FC<{ m: EquipoMensaje; onAbrir: () => void }> = ({ m, onAbrir }) => {
	const ext = (m.file_name || "").split(".").pop()?.toLowerCase() || "";
	return (
		<button type="button" className="eqm-doc" onClick={onAbrir}>
			<span className={"eqm-doc-ico " + (ICONO_EXT[ext] || "file")}><WappIcono n={ICONO_EXT[ext] || "file"} /></span>
			<span className="eqm-doc-tx"><b>{m.file_name || "Documento"}</b><small>{[ext.toUpperCase().slice(0, 4), tamano(m.file_size)].filter(Boolean).join(" · ")}</small></span>
		</button>
	);
};

/**
 * Nota de voz / audio: reproductor del navegador, contenido. La URL firmada vence a los 30 min: con la conversación abierta
 * más tiempo, al darle play no sonaba nada (Christy → Keily, 2026-10-13). Si al darle play ya pasó el rato, o si el
 * navegador marca error, se pide una URL nueva y sigue donde iba.
 */
const Audio: React.FC<{ campusId: number; m: EquipoMensaje }> = ({ campusId, m }) => {
	const [url, setUrl] = useState<string | null>(null);
	const el = useRef<HTMLAudioElement>(null);
	const pedidaEn = useRef(0);
	const renovando = useRef(false);
	useEffect(() => {
		let vivo = true;
		urlDe(campusId, m.id).then((d) => { if (vivo) { pedidaEn.current = cache.get(campusId + ":" + m.id)?.at || Date.now(); setUrl(d.url); } }).catch(() => null);
		return () => { vivo = false; };
	}, [campusId, m.id]);
	const renovar = (seguir: boolean) => {
		const a = el.current;
		if (!a || renovando.current) return;
		renovando.current = true;
		const t = a.currentTime;
		cache.delete(campusId + ":" + m.id);
		urlDe(campusId, m.id).then((d) => {
			pedidaEn.current = Date.now();
			a.src = d.url;
			a.addEventListener("loadedmetadata", () => {
				renovando.current = false;
				if (t) a.currentTime = t;
				if (seguir) a.play().catch(() => null);
			}, { once: true });
			a.load();
		}).catch(() => { renovando.current = false; });
	};
	const alPlay = () => {
		if (Date.now() - pedidaEn.current > 20 * 60000) { el.current?.pause(); renovar(true); }
	};
	return url ? <audio ref={el} className="eqm-audio" controls controlsList="nodownload" preload="metadata" src={url} onPlay={alPlay} onError={() => renovar(false)} />
		: <span className="eqm-cargando audio" />;
};

/** Sticker: la URL estable viene con el mensaje; solo si no (archivo en el disco del servidor) se pide como cualquier archivo. */
const StickerDeMensaje: React.FC<{ campusId: number; m: EquipoMensaje }> = ({ campusId, m }) => {
	const [url, setUrl] = useState<string | null>(m.sticker_url || null);
	useEffect(() => {
		if (m.sticker_url) { setUrl(m.sticker_url); return; }
		let vivo = true;
		urlDe(campusId, m.id).then((d) => { if (vivo) setUrl(d.url); }).catch(() => null);
		return () => { vivo = false; };
	}, [campusId, m.id, m.sticker_url]);
	return <StickerImg url={url} />;
};

/** El archivo de UN mensaje (foto, video, voz o documento). */
export const MedioMensaje: React.FC<{ campusId: number; m: EquipoMensaje; todos: EquipoMensaje[] }> = ({ campusId, m, todos }) => {
	const abrir = () => abrirGaleria(campusId, todos, m);
	if (m.file_tipo === "audio") return <Audio campusId={campusId} m={m} />;
	if (m.file_tipo === "sticker") return <StickerDeMensaje campusId={campusId} m={m} />;
	if (esVisual(m)) return <Miniatura campusId={campusId} m={m} onAbrir={abrir} clase="sola" />;
	return <TarjetaArchivo m={m} onAbrir={abrir} />;
};

/** Varias fotos/videos seguidos de la misma persona: cuadrícula (máximo 4 a la vista, "+N" en la última). */
export const Album: React.FC<{ campusId: number; items: EquipoMensaje[]; todos: EquipoMensaje[] }> = ({ campusId, items, todos }) => {
	const vista = items.slice(0, 4);
	const resto = items.length - vista.length;
	return (
		<div className={"eqm-album n" + Math.min(4, items.length)}>
			{vista.map((m, i) => <Miniatura key={m.id} campusId={campusId} m={m} onAbrir={() => abrirGaleria(campusId, todos, m)}
				extra={i === vista.length - 1 && resto > 0 ? resto : undefined} />)}
		</div>
	);
};

/**
 * Agrupa la lista de mensajes: fotos/videos SIN texto seguidos de la misma persona en menos de 3 min forman un álbum
 * (así salen cuando se mandan varios a la vez). Devuelve, por mensaje, si encabeza un álbum (con sus miembros) o si
 * va dentro de uno (y no se pinta solo).
 */
export const agruparAlbumes = (lista: EquipoMensaje[]): { cabeza: Map<number, EquipoMensaje[]>; dentro: Set<number> } => {
	const cabeza = new Map<number, EquipoMensaje[]>();
	const dentro = new Set<number>();
	const t = (m: EquipoMensaje) => new Date(m.created_at.replace(" ", "T") + (m.created_at.includes("Z") || m.created_at.includes("+") ? "" : "Z")).getTime();
	let grupo: EquipoMensaje[] = [];
	const cerrar = () => {
		if (grupo.length > 1) { cabeza.set(grupo[0].id, grupo); grupo.slice(1).forEach((x) => dentro.add(x.id)); }
		grupo = [];
	};
	for (const m of lista) {
		const apto = esVisual(m) && !m.deleted_at && !m.reply_to_id && !m.prospect_id;
		const ultimo = grupo[grupo.length - 1];
		// El texto (comentario) puede ir en la primera foto del álbum; las siguientes van sin texto.
		if (apto && ultimo && ultimo.user_id === m.user_id && !m.body && Math.abs(t(m) - t(ultimo)) < 180000) { grupo.push(m); continue; }
		cerrar();
		if (apto) grupo = [m];
	}
	cerrar();
	return { cabeza, dentro };
};

// ------------------------------------------------------------------ enviar varios

export interface Elegido { id: string; file: File; url: string | null; tipo: "image" | "video" | "document"; }
export const tipoDeArchivo = (f: File): Elegido["tipo"] => {
	const n = (f.name || "").toLowerCase();
	if (f.type.startsWith("video/") || /\.(mov|mp4|m4v|3gp|webm)$/.test(n)) return "video";
	if (f.type.startsWith("image/") || /\.(jpe?g|png|gif|webp|heic|heif)$/.test(n)) return "image";
	return "document";
};
export const MAX_ARCHIVOS = 20;
export const elegidosDe = (files: FileList | File[] | null | undefined): Elegido[] =>
	Array.from(files || []).slice(0, MAX_ARCHIVOS).map((f, i) => {
		const tipo = tipoDeArchivo(f);
		return { id: Date.now() + "-" + i + "-" + f.name, file: f, tipo, url: tipo === "document" ? null : URL.createObjectURL(f) };
	});

/**
 * Bandeja antes de mandar: miniaturas, quitar uno, comentario opcional y Enviar. Se mandan en orden (el comentario va con
 * el primero) y quedan juntos como álbum.
 */
export const BandejaEnvio: React.FC<{
	elegidos: Elegido[]; onQuitar: (id: string) => void; onAgregar: (f: FileList) => void; onCancelar: () => void;
	onEnviar: (comentario: string, menciones: number[]) => void; progreso: { i: number; n: number; f: number } | null; error: string | null; compacta?: boolean;
	/** WappTeam: @menciones en el comentario (WappChat no lo pasa: es al cliente). */
	mencionar?: { campusId: number; canalId: number };
}> = ({ elegidos, onQuitar, onAgregar, onCancelar, onEnviar, progreso, error, compacta, mencionar }) => {
	const [comentario, setComentario] = useState("");
	const [elegidas, setElegidas] = useState<Persona[]>([]);
	const mas = useRef<HTMLInputElement>(null);
	const cuadro = useRef<HTMLTextAreaElement>(null);
	const enviando = progreso !== null;
	const men = useMenciones(mencionar?.campusId || 0, mencionar?.canalId || null);
	useCrecer(cuadro, comentario, 4);
	const mandar = () => { if (!enviando && elegidos.length) onEnviar(comentario, idsMencionados(elegidas, comentario)); };
	const elegirMencion = (p: Persona) => {
		men.poner(cuadro.current, comentario, p, setComentario);
		setElegidas((prev) => (prev.some((x) => x.id === p.id) ? prev : [...prev, { id: p.id, nombre: primerNombre(p) }]));
	};
	return (
		<div className={"eqm-bandeja" + (compacta ? " compacta" : "")}>
			<div className="eqm-bandeja-items">
				{elegidos.map((e) => (
					<div key={e.id} className="eqm-bandeja-item" title={e.file.name}>
						{e.tipo === "image" && e.url ? <img src={e.url} alt="" />
							: e.tipo === "video" && e.url ? <><video src={e.url + "#t=0.001"} muted playsInline preload="metadata" /><span className="eqm-play chico"><svg viewBox="0 0 24 24" width="14" height="14"><path d="M8 5.5v13l11-6.5z" fill="currentColor" /></svg></span></>
							: <span className="eqm-bandeja-doc"><WappIcono n={ICONO_EXT[(e.file.name.split(".").pop() || "").toLowerCase()] || "file"} /><small>{e.file.name}</small></span>}
						{!enviando ? <button type="button" className="eqm-quitar" onClick={() => onQuitar(e.id)} aria-label={"Quitar " + e.file.name}><WappIcono n="x" /></button> : null}
					</div>
				))}
				{!enviando && elegidos.length < MAX_ARCHIVOS ? (
					<button type="button" className="eqm-bandeja-mas" onClick={() => mas.current?.click()} aria-label="Agregar más archivos"><WappIcono n="plus" /></button>
				) : null}
				<input ref={mas} type="file" multiple hidden onChange={(e) => { if (e.target.files) onAgregar(e.target.files); e.target.value = ""; }} />
			</div>
			{progreso ? (
				<div className="eqm-progreso"><span style={{ width: Math.round(((progreso.i + progreso.f) / progreso.n) * 100) + "%" }} /></div>
			) : null}
			{error ? <div className="eqm-bandeja-error" role="alert">{error}</div> : null}
			<ListaMenciones sugeridas={men.sugeridas} activa={men.activa} onActiva={men.setActiva} onElegir={elegirMencion} />
			<div className="eqm-bandeja-pie">
				{/* Enter manda; Shift+Enter (o Ctrl/Alt) baja de renglón, igual que el cuadro de escribir (Nancy, 2026-10-15). */}
				<textarea ref={cuadro} rows={1} value={comentario} placeholder={mencionar ? "Comentario (opcional, @ para mencionar)" : "Comentario (opcional)"}
					aria-label="Comentario" maxLength={1000} disabled={enviando} autoFocus
					onChange={(e) => { setComentario(e.target.value); men.revisar(e.target.value, e.target.selectionStart); }}
					onKeyDown={(e) => {
						if (men.tecla(e, elegirMencion)) return;
						if (e.key === "Escape" && !enviando) { onCancelar(); return; }
						teclaEnter(e, mandar);
					}} />
				{/* Emojis en el comentario del archivo (Esteban, 2026-10-16, Nancy). */}
				<BotonEmoji compacto disabled={enviando} onElegir={(em) => insertarEnCursor(cuadro.current, comentario, em, setComentario)} />
				<button type="button" className="btn-wa" onClick={onCancelar} disabled={enviando}>Cancelar</button>
				<button type="button" className="btn-wa pri" onClick={mandar} disabled={enviando || !elegidos.length}>
					{enviando ? "Enviando " + (progreso!.i + 1) + " de " + progreso!.n : "Enviar" + (elegidos.length > 1 ? " " + elegidos.length : "")}
				</button>
			</div>
		</div>
	);
};

/**
 * Manda los elegidos uno tras otro (en orden, para que el álbum quede igual), el comentario con el primero.
 * subir(file, name, kind, comentario, onProgress).
 */
export const enviarVarios = async (elegidos: Elegido[], comentario: string,
	subir: (f: File, nombre: string, kind: "media" | "document", texto: string, onProgress: (x: number) => void) => Promise<any>,
	onProgreso: (p: { i: number; n: number; f: number }) => void): Promise<string | null> => {
	for (let i = 0; i < elegidos.length; i++) {
		const e = elegidos[i];
		if (e.file.size > 200 * 1048576) return "\"" + e.file.name + "\" pasa de 200 MB.";
		onProgreso({ i, n: elegidos.length, f: 0 });
		try {
			await subir(e.file, e.file.name || (e.tipo === "video" ? "video.mp4" : e.tipo === "image" ? "foto.jpg" : "archivo"),
				e.tipo === "document" ? "document" : "media", i === 0 ? comentario.trim() : "", (f) => onProgreso({ i, n: elegidos.length, f }));
		} catch (err: any) {
			return (err?.response?.status === 413 ? "\"" + e.file.name + "\" es demasiado grande." : errorTexto(err)) + (i ? " (se mandaron " + i + " de " + elegidos.length + ")" : "");
		}
	}
	return null;
};

/** Archivos pegados (Ctrl+V de una captura) o arrastrados encima: los entrega a onArchivos. */
export const useArchivosEntrantes = (ref: React.RefObject<HTMLElement>, activo: boolean, onArchivos: (f: File[]) => void) => {
	const [encima, setEncima] = useState(false);
	const cb = useRef(onArchivos);
	cb.current = onArchivos;
	useEffect(() => {
		const el = ref.current;
		if (!el || !activo) return;
		const pegar = (e: ClipboardEvent) => {
			const fs = Array.from(e.clipboardData?.files || []);
			if (fs.length) { e.preventDefault(); cb.current(fs); }
		};
		let cuenta = 0;
		const entra = (e: DragEvent) => { if (e.dataTransfer?.types?.includes("Files")) { e.preventDefault(); cuenta++; setEncima(true); } };
		const sobre = (e: DragEvent) => { if (e.dataTransfer?.types?.includes("Files")) e.preventDefault(); };
		const sale = () => { cuenta = Math.max(0, cuenta - 1); if (!cuenta) setEncima(false); };
		const suelta = (e: DragEvent) => {
			if (!e.dataTransfer?.files?.length) return;
			e.preventDefault(); cuenta = 0; setEncima(false);
			cb.current(Array.from(e.dataTransfer.files));
		};
		el.addEventListener("paste", pegar);
		el.addEventListener("dragenter", entra);
		el.addEventListener("dragover", sobre);
		el.addEventListener("dragleave", sale);
		el.addEventListener("drop", suelta);
		return () => {
			el.removeEventListener("paste", pegar); el.removeEventListener("dragenter", entra); el.removeEventListener("dragover", sobre);
			el.removeEventListener("dragleave", sale); el.removeEventListener("drop", suelta);
		};
	}, [ref, activo]);
	return encima;
};

// ------------------------------------------------------------------ palomitas y reacciones

/** Palomitas como WhatsApp/WappChat: ✓ enviado, ✓✓ entregado, ✓✓ azules leído. */
export const Palomitas: React.FC<{ estado: "enviado" | "entregado" | "leido"; onClick?: () => void }> = ({ estado, onClick }) => {
	const label = estado === "leido" ? "Leído" : estado === "entregado" ? "Entregado" : "Enviado";
	const svg = estado === "enviado"
		? <svg viewBox="0 0 16 11" width="15" height="11" aria-hidden="true"><path d="M1.5 6l3.5 3.5L14 1" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" /></svg>
		: <svg viewBox="0 0 20 11" width="18" height="11" aria-hidden="true"><path d="M1 6l3.5 3.5L13 1M8.5 8.5l1 1L18 1" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" /></svg>;
	return onClick
		? <button type="button" className={"cv-tick eqm-tick " + estado} title={label + " · ver info"} aria-label={label + ", ver info del mensaje"} onClick={onClick}>{svg}</button>
		: <span className={"cv-tick eqm-tick " + estado} title={label} aria-label={label}>{svg}</span>;
};

export const estadoPalomitas = (m: EquipoMensaje, vistoHasta: number | null, entregadoHasta: number | null): "enviado" | "entregado" | "leido" =>
	vistoHasta !== null && vistoHasta >= m.id ? "leido" : entregadoHasta !== null && entregadoHasta >= m.id ? "entregado" : "enviado";

/** Reacciones como WhatsApp: pastilla sobre el borde de abajo de la burbuja; al tocarla, quién reaccionó. */
export const PastillaReacciones: React.FC<{ reacciones: EquipoMensaje["reacciones"]; onReaccionar?: (emoji: string) => void }> = ({ reacciones, onReaccionar }) => {
	const [ver, setVer] = useState(false);
	if (!reacciones.length) return null;
	const total = reacciones.reduce((t, r) => t + r.n, 0);
	return (
		<span className="eqm-reac-caja">
			<button type="button" className="eqm-reac" onClick={(e) => { e.stopPropagation(); setVer(!ver); }} aria-label={"Reacciones: " + total} aria-expanded={ver}>
				{reacciones.slice(0, 3).map((r) => <span key={r.emoji}>{r.emoji}</span>)}{total > 1 ? <b>{total}</b> : null}
			</button>
			{ver ? (
				<>
					<span className="eqm-reac-velo" onClick={(e) => { e.stopPropagation(); setVer(false); }} />
					<span className="eqm-reac-quien" role="dialog" onClick={(e) => e.stopPropagation()}>
						{reacciones.map((r) => (
							<span key={r.emoji} className="eqm-reac-fila">
								<span className="e">{r.emoji}</span><span className="q">{r.quienes.join(", ")}</span>
								{onReaccionar && r.mia ? <button type="button" className="enlace" onClick={() => { setVer(false); onReaccionar(r.emoji); }}>Quitar</button> : null}
							</span>
						))}
					</span>
				</>
			) : null}
		</span>
	);
};
